import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";
import parsePhoneNumber from "libphonenumber-js";

import { toast } from "react-toastify";
import { getCurrentUser, updateUser } from "api";

// import components
import Input from "components/atoms/Input/Input";
import Button from "components/atoms/Button/Button";
import CardNew from "components/molecules/Card/CardNew";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";

// import helpers
import { isValueExist } from "helpers/validationHelpers";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import usePermissions from "hooks/usePermissions";
import useTimezoneSelectOptions from "hooks/useTimezoneSelectOptions";
import { resolveAppLocale } from "helpers/localeHelpers";
import UpdatePasswordDialog from "./UpdatePasswordDialog";
import { dateFormatOptions, languageOptions, unitOptions, findOptionByLabel } from "./options";

import "./yourDetail.scss";

const YourDetails = function ({ currentLocale, handleLocaleChange, setPermissions }) {
  const [showDialog, setShowDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customBrokerTeams, setCustomBrokerTeams] = useState([]);
  const [providerTeams, setProviderTeams] = useState([]);
  const [email, setEmail] = useState("");
  const methods = useForm();
  const intl = useIntl();
  const isSmsNotificationEnabled = methods.watch("sms_notification");
  const timezoneData = useTimezoneSelectOptions();

  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions);

  useEffect(() => {
    getCurrentUser()
      .then((res) => {
        setCustomBrokerTeams(res.data.custom_broker_teams);
        setProviderTeams(res.data.provider_team);
        setEmail(res.data.email);
        const language = resolveAppLocale(res.data.language);
        methods.reset({
          email: res.data.email,
          first_name: res.data.first_name,
          last_name: res.data.last_name,
          phone_number: res.data.phone_number,
          sms_notification: res.data.sms_notification,
          sms_number: res.data.sms_number,
          timezone: res.data.timezone,
          language,
          unit: findOptionByLabel(unitOptions, res.data.unit)?.value,
          date_format: findOptionByLabel(dateFormatOptions, res.data.date_format)?.value,
        });
        if (currentLocale !== language) {
          handleLocaleChange(language);
        }
      })
      .catch((err) => {
        errorToast(
          err,
          intl.formatMessage({
            id: "settings.couldNotRetrieveSettings",
            defaultMessage: "Could not retrieve settings. Please try again later",
          }),
        );
      });
  }, []);

  const handleClose = () => {
    setShowDialog(false);
  };

  const handleSaveDetails = (data) => {
    setIsSubmitting(true);
    const bodyData = {
      first_name: data.first_name,
      last_name: data.last_name,
      phone_number: data.phone_number,
      sms_notification: data.sms_notification,
      ...(data.sms_notification && { sms_number: data.sms_number }),
      timezone: data.timezone,
      language: data.language,
      unit: data.unit,
      date_format: data.date_format,
    };

    updateUser(bodyData)
      .then(() => {
        setIsSubmitting(false);
        if (currentLocale !== data.language) {
          handleLocaleChange(data.language);
        }
        toast(
          intl.formatMessage({
            id: "settings.your-details.profileUpdateSuccessMessage",
            defaultMessage: "Profile updated sucessfully",
          }),
          { type: "success" },
        );

        const newPermissions = {
          ...JSON.parse(permissions),
          first_name: data.first_name,
          last_name: data.last_name,
          full_name: `${data.first_name} ${data.last_name}`,
          preferred_timezone: data.timezone,
        };

        if (setPermissions) setPermissions(JSON.stringify(newPermissions));
        localStorage.setItem("cargobase_userpermissions", JSON.stringify(newPermissions));
      })
      .catch((err) => {
        setIsSubmitting(false);
        errorToast(
          err,
          intl.formatMessage({
            id: "settings.your-details.couldNotSaveSettings",
            defaultMessage: "Could not save settings. Please try again later",
          }),
        );
      });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSaveDetails)}>
        <div style={{ maxWidth: "848px" }}>
          <CardNew>
            <Row
              style={{
                padding: "16px 0",
              }}
            >
              <Col sm={3}>
                <h6>
                  <FormattedMessage id="settings.your-details.loginAndPassword" defaultMessage="Login and password" />
                </h6>
                <span className="text-secondary font-b3">
                  <FormattedMessage id="settings.your-details.loginCredentials" defaultMessage="Login credentials" />
                </span>
              </Col>
              <Col sm={9} className="d-flex flex-column flex-lg-row">
                <div className="d-flex flex-column">
                  <div className="font-b3 mb-1 mt-3 mt-md-0">
                    <FormattedMessage id="settings.your-details.emailOrUsernameLabel" defaultMessage="Email / Username" />
                  </div>
                  <Input
                    className="rounded-input input-width-md"
                    type="email"
                    name="email"
                    eventContext="Settings - Your Details"
                    disabled
                  />
                </div>
                <div className="d-flex align-items-end">
                  <Button
                    style={{
                      height: "26px",
                      backgroundColor: "#8BCAFD",
                      border: "none",
                    }}
                    className="rounded-pill ml-lg-3 mt-3 mt-lg-0"
                    onClick={() => {
                      setShowDialog(true);
                    }}
                  >
                    <FormattedMessage id="settings.your-details.updatePassword" defaultMessage="Update password" />
                  </Button>
                </div>
              </Col>
            </Row>
            <Row
              style={{
                padding: "16px 0",
              }}
            >
              <Col sm={3}>
                <h6>
                  <FormattedMessage id="settings.your-details.contactDetails" defaultMessage="Contact Details" />
                </h6>
                <span className="text-secondary font-b3">
                  <FormattedMessage id="tracking.contactDetails" defaultMessage="Your contact details" />
                </span>
              </Col>
              <Col sm={9}>
                <div className="d-flex flex-column flex-lg-row">
                  <div>
                    <div className="font-b3 mb-1 mt-3 mt-md-0">
                      <FormattedMessage id="settings.your-details.firstNameLabel" defaultMessage="First name" />
                    </div>
                    <Input
                      className="input-width-md rounded-input"
                      type="text"
                      name="first_name"
                      eventContext="Settings - Your Details"
                      required
                      validations={{
                        validate: { valueExist: isValueExist },
                      }}
                      placeholder={intl.formatMessage({
                        id: "settings.your-details.firstNameLabel",
                        defaultMessage: "First name",
                      })}
                    />
                  </div>
                  <div className="ml-lg-3 mt-md-3 mt-3 mt-lg-0">
                    <div className="font-b3 mb-1 ">
                      <FormattedMessage id="settings.your-details.lastNameLabel" defaultMessage="Last name" />
                    </div>
                    <Input
                      className="input-width-md rounded-input"
                      type="text"
                      name="last_name"
                      eventContext="Settings - Your Details"
                      required
                      validations={{
                        validate: { valueExist: isValueExist },
                      }}
                      placeholder={intl.formatMessage({
                        id: "settings.your-details.lastNameLabel",
                        defaultMessage: "Last name",
                      })}
                    />
                  </div>
                  <div className="ml-lg-3 mt-md-3 mt-3 mt-lg-0">
                    <div className="font-b3 mb-1 ">
                      <FormattedMessage id="settings.your-details.phoneNumberLabel" defaultMessage="Phone number" />
                    </div>
                    <Input
                      className="input-width-md rounded-input"
                      type="text"
                      name="phone_number"
                      eventContext="Settings - Your Details"
                      required
                      validations={{
                        validate: { valueExist: isValueExist },
                        pattern: {
                          value: /^[+()0-9\s-]*$/g,

                          message: intl.formatMessage({
                            id: "general.invalidPhoneNumber",
                            defaultMessage: "Invalid phone number",
                          }),
                        },
                      }}
                      placeholder={intl.formatMessage({
                        id: "settings.your-details.phoneNumberPlaceholder",
                        defaultMessage: "Phone Number (incl. country code)",
                      })}
                    />
                  </div>
                </div>
                <div>
                  <div className="mt-15 font-b3">
                    <Checkbox
                      providerSections
                      checkboxLabel={intl.formatMessage({
                        id: "settings.your-details.smsNotificationLabel",
                        defaultMessage: "SMS notifications",
                      })}
                      name="sms_notification"
                    />
                  </div>
                  <div className="mt-15">
                    <div className="mb-1 font-b3">
                      <FormattedMessage
                        id="settings.your-details.smsNotificationNumberLabel"
                        defaultMessage="SMS notifications number"
                      />
                    </div>

                    <Input
                      disabled={!isSmsNotificationEnabled}
                      required={isSmsNotificationEnabled}
                      eventContext="Settings - Your Details"
                      className="rounded-input input-width-md"
                      validations={{
                        validate: {
                          valueExist: (value) => isValueExist(value) || !isSmsNotificationEnabled,
                          validatePhoneNumber: (value) => {
                            if (!isSmsNotificationEnabled) return true;
                            const phoneNumber = parsePhoneNumber(value);
                            return (
                              (phoneNumber && phoneNumber.isValid()) ||
                              "Please enter a valid phone number including country code"
                            );
                          },
                        },
                      }}
                      type="text"
                      name="sms_number"
                      placeholder={intl.formatMessage({
                        id: "settings.your-details.smsNotificationNumberPlaceholder",
                        defaultMessage: "Phone number for SMS notification",
                      })}
                    />
                  </div>
                </div>
              </Col>
            </Row>
            <Row
              style={{
                padding: "16px 0",
              }}
            >
              <Col sm={3}>
                <h6>
                  <FormattedMessage id="settings.your-details.languageAndTimezone" defaultMessage="Language and timezone" />
                </h6>
                <span className="text-secondary font-b3">
                  <FormattedMessage
                    id="settings.your-details.languageSubtext"
                    defaultMessage="Your preferred language and timezone"
                  />
                </span>
              </Col>
              <Col sm={9} className="d-flex flex-column flex-lg-row">
                <div>
                  <div className="font-b3 mb-1 mt-3 mt-md-0">
                    <FormattedMessage
                      id="settings.your-details.preferredTimezoneLabel"
                      defaultMessage="Preferred timezone"
                    />
                  </div>
                  <div className="w-192-px">
                    <SearchSelect useHookForm options={timezoneData} name="timezone" placeholder="Please select" />
                  </div>
                </div>
                <div className="ml-0 ml-lg-3 mt-3 mt-md-0">
                  <div className="font-b3 mb-1">
                    <FormattedMessage id="settings.your-details.languageLabel" defaultMessage="Language" />
                  </div>
                  <div className="w-192-px">
                    <Dropdown useHookForm name="language" options={languageOptions} placeholder="Please select" />
                  </div>
                </div>
              </Col>
            </Row>
            <Row
              style={{
                padding: "16px 0",
              }}
            >
              <Col sm={3}>
                <h6>
                  <FormattedMessage id="settings.your-details.format" defaultMessage="Format" />
                </h6>
                <span className="text-secondary font-b3">
                  <FormattedMessage
                    id="settings.your-details.unitSystemSubtext"
                    defaultMessage="Your preferred unit and date format"
                  />
                </span>
              </Col>
              <Col sm={9} className="d-flex flex-column flex-lg-row">
                <div>
                  <div className="font-b3 mb-1 mt-3 mt-md-0">
                    <FormattedMessage id="settings.your-details.unitSystemLabel" defaultMessage="Unit system default" />
                  </div>
                  <div className="w-192-px">
                    <Dropdown useHookForm name="unit" options={unitOptions} placeholder="Please select" />
                  </div>
                </div>
                <div className="ml-0 ml-lg-3">
                  <div className="font-b3 mb-1 mt-3 mt-md-0">
                    <FormattedMessage id="settings.your-details.dateFormatLabel" defaultMessage="Date format for reports" />
                  </div>
                  <div className="w-192-px">
                    <Dropdown useHookForm placeholder="Please select" options={dateFormatOptions} name="date_format" />
                  </div>
                </div>
              </Col>
            </Row>

            {user_type === "provider" && (
              <Row
                style={{
                  padding: "16px 0",
                }}
              >
                <Col sm={3}>
                  <h6>
                    <FormattedMessage id="general.teams" defaultMessage="Teams" />
                  </h6>
                  <span className="text-secondary font-b3">
                    <FormattedMessage
                      id="settings.your-details.yourTeams"
                      defaultMessage="Your teams and customs broker teams"
                    />
                  </span>
                </Col>
                <Col sm={9} className="d-flex flex-column flex-lg-row">
                  <div className="w-192-px">
                    <div className="font-b3 mb-1">
                      <div className="font-b4 mb-1">
                        <FormattedMessage id="general.teams" defaultMessage="Teams" />
                      </div>
                      {providerTeams.map((team) => (
                        <p className="font-b3 mt-1">{team.team_name}</p>
                      ))}
                    </div>
                  </div>
                  <div className="ml-0 ml-lg-3 w-192-px">
                    <div className="font-b4 mb-1">
                      {" "}
                      <FormattedMessage id="settings.user.customBrokerTeams" defaultMessage="Custom broker teams" />
                    </div>
                    {customBrokerTeams.map((team) => (
                      <p className="font-b3 mt-1">{team.team_name}</p>
                    ))}
                  </div>
                </Col>
              </Row>
            )}

            <Button
              type="submit"
              variant="primary"
              className="ml-auto rounded-pill"
              style={{ height: "26px" }}
              disabled={isSubmitting}
              data-testid={testIds.YOUR_DETAILS_SAVE_BUTTON}
            >
              <FormattedMessage
                id={isSubmitting ? "general.saving" : "general.save"}
                defaultMessage={isSubmitting ? "Saving" : "Save"}
              />
            </Button>
          </CardNew>
        </div>
      </form>
      <UpdatePasswordDialog showDialog={showDialog} handleClose={handleClose} email={email} />
    </FormProvider>
  );
};

YourDetails.propTypes = {
  currentLocale: PropTypes.string,
  handleLocaleChange: PropTypes.func,
  setPermissions: PropTypes.func.isRequired,
};

YourDetails.defaultProps = {
  currentLocale: "en",
  handleLocaleChange: null,
};

export default YourDetails;
